import { useEffect, useRef, useState } from "react";
import { Alert, Input, Modal, Typography } from "antd";
import type { InputRef } from "antd";
import { SafetyOutlined } from "@ant-design/icons";

const { Text } = Typography;

interface GaConfirmModalProps {
  open: boolean;
  loading?: boolean;
  error?: string | null;
  onConfirm: (code: string) => void;
  onCancel: () => void;
}

/**
 * Step-up TOTP prompt (docs/305 §6.4). Rendered once by <GaProvider>; opened
 * automatically whenever the API returns 428.
 */
export default function GaConfirmModal({
  open,
  loading = false,
  error = null,
  onConfirm,
  onCancel,
}: GaConfirmModalProps) {
  const [code, setCode] = useState("");
  const inputRef = useRef<InputRef>(null);

  useEffect(() => {
    if (open) {
      setCode("");
      const t = setTimeout(() => inputRef.current?.focus(), 100);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open]);

  const handleOk = (): void => {
    if (code.length === 6) onConfirm(code);
  };

  return (
    <Modal
      open={open}
      title={
        <span>
          <SafetyOutlined style={{ color: "#faad14", marginRight: 8 }} />
          Step-up verification
        </span>
      }
      okText="Confirm"
      cancelText="Cancel"
      onOk={handleOk}
      onCancel={onCancel}
      okButtonProps={{ disabled: code.length !== 6, loading }}
      destroyOnClose
    >
      <div style={{ marginBottom: 16 }}>
        <Text type="secondary">
          This is a high-risk operation. Enter the 6-digit code from your authenticator app.
        </Text>
      </div>
      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}
      <Input
        ref={inputRef}
        placeholder="6-digit code"
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
        onPressEnter={handleOk}
        maxLength={6}
        size="large"
        style={{ textAlign: "center", fontSize: 24, letterSpacing: 8 }}
        autoComplete="one-time-code"
        inputMode="numeric"
      />
    </Modal>
  );
}
